import * as React from 'react';
import { useHistory } from 'react-router-dom';

import './Dashboard.css';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import Page from '@app/components/Page';
import { Col, Row } from 'reactstrap';
import ApiGateway from '@app/api/api-gateway/api-gateway';

import { MdLightbulbOutline, MdNetworkCell, MdOutlineImportExport, MdLanguage } from 'react-icons/md';
import IconWidget from '@app/components/widget/IconWidget';
import Loader from '@app/components/Loader';
import GoogleMapWithBubblesDashboard from '@app/components/GoogleMapWithBubblesDashboard';
import { Link } from 'react-router-dom';
import { useMapData } from '@app/contexts/mapData.context';

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

type VersionKey = 'caflex' | 'caimage' | 'controller' | 'gateway';

type Versions = Record<VersionKey, string>;

const unknownVersions: Versions = {
  caflex: 'Unknown',
  caimage: 'Unknown',
  controller: 'Unknown',
  gateway: 'Unknown',
};

const versionItems: Array<{ key: VersionKey; label: string }> = [
  { key: 'caflex', label: 'CAFlex' },
  { key: 'caimage', label: 'CAImage' },
  { key: 'controller', label: 'Controller' },
  { key: 'gateway', label: 'Gateway' },
];

const DashboardView = (): React.ReactElement => {
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;
  const { state: mapState, loadDashboardData } = useMapData();
  const history = useHistory();
  const [activeAlertTab, setActiveAlertTab] = React.useState<'sites' | 'controllers' | 'gateways'>('sites');
  const [versions, setVersions] = React.useState<Versions>(unknownVersions);

  // Add a ref to track if the component is mounted
  const isMounted = React.useRef(true);

  // Separate loading states for different sections
  const [loadingStates, setLoadingStates] = React.useState({
    alerts: true,
    heatmap: true,
    overview: true,
    versionControl: true,
  });

  React.useEffect(() => {
    let cancelled = false;

    const loadVersions = async () => {
      try {
        const response = await ApiGateway.get('/updates/versions');

        if (!cancelled) {
          setVersions((previousVersions) => {
            const nextVersions = response.data?.versions || {};

            return versionItems.reduce(
              (result, item) => ({
                ...result,
                [item.key]: nextVersions[item.key] || previousVersions[item.key] || 'Unknown',
              }),
              previousVersions
            );
          });
        }
      } catch (error) {
        console.error('Error loading versions:', error);
        // Keep the previously loaded versions. Initial values are "Unknown".
      } finally {
        if (!cancelled) {
          setLoadingStates((previous) => ({ ...previous, versionControl: false }));
        }
      }
    };

    loadVersions();

    return () => {
      cancelled = true;
    };
  }, []);

  // Helper function to get status color based on status code
  const getStatusColor = (statusCode) => {
    switch (statusCode) {
      case 0:
        return 'status-unknown'; // Unknown - Black
      case 1:
        return 'status-down'; // Down - Red
      case 2:
        return 'status-warning'; // Warning - Yellow
      case 3:
        return 'status-good'; // Good - Green
      case 4:
        return 'status-missing'; // Missing - Gray
      default:
        return 'status-unknown'; // Unknown status
    }
  };

  // Load cached data on mount
  React.useEffect(() => {
    const initializeData = async () => {
      try {
        // Only call loadDashboardData if we don't already have cached data
        // AND cache initialization is complete
        if (!mapState.dashboardData && mapState.cacheInitialized) {
          await loadDashboardData();
        }

        if (isMounted.current) {
          // Update loading states progressively
          setLoadingStates((prev) => ({
            ...prev,
            alerts: false,
            heatmap: false,
            overview: false,
          }));
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        // Update loading states even on error
        setLoadingStates((prev) => ({
          ...prev,
          alerts: false,
          heatmap: false,
          overview: false,
        }));
      }
    };

    // Only initialize if cache is ready
    if (mapState.cacheInitialized) {
      initializeData();
    }

    return () => {
      isMounted.current = false;
    };
  }, [loadDashboardData, mapState.dashboardData, mapState.cacheInitialized]);

  React.useEffect(() => {
    if (mapState.dashboardData && !mapState.dashboardLoading) {
      if (process.env.NODE_ENV === 'development') {
        console.log('DashboardView: Data available, resetting loading states');
      }
      setLoadingStates((prev) => ({
        ...prev,
        alerts: false,
        heatmap: false,
        overview: false,
      }));
    }
  }, [mapState.dashboardData, mapState.dashboardLoading]);

  // Add debugging
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('DashboardView: Loading states debug:', {
        'loadingStates.alerts': loadingStates.alerts,
        'loadingStates.heatmap': loadingStates.heatmap,
        'loadingStates.overview': loadingStates.overview,
        'loadingStates.versionControl': loadingStates.versionControl,
        'mapState.dashboardLoading': mapState.dashboardLoading,
        'mapState.dashboardData exists': !!mapState.dashboardData,
        'mapState.sites.length': mapState.sites?.length || 0,
        'mapState.controllers.length': mapState.controllers?.length || 0,
      });
    }
  }, [loadingStates, mapState.dashboardLoading, mapState.dashboardData, mapState.sites, mapState.controllers]);

  // Make sure to set isMounted to false when component unmounts
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Process data from cache
  const processedSites = React.useMemo(() => {
    return (mapState.sites || []).map((site) => ({
      ...site,
      displayName: site.name || site.id,
      statusColor: getStatusColor(site.status),
    }));
  }, [mapState.sites]);

  const processedControllers = React.useMemo(() => {
    return (mapState.controllers || []).map((controller) => ({
      ...controller,
      displayName: controller.name || controller.id,
      statusColor: getStatusColor(controller.status),
    }));
  }, [mapState.controllers]);

  const processedGateways = React.useMemo(() => {
    return (mapState.dashboardData?.partner?.gateways?.gateways || []).map((gateway) => ({
      ...gateway,
      statusColor: getStatusColor(gateway.status),
    }));
  }, [mapState.dashboardData]);

  // Memoize map props to prevent unnecessary re-renders that cause flashing
  const mapControllers = React.useMemo(() => {
    return processedControllers.map((item) => ({
      ...item,
      displayText: item.displayName || item.name || item.id,
      id: item.id,
      type: 'controller' as const,
    }));
  }, [processedControllers]);

  const mapSites = React.useMemo(() => {
    return processedSites.map((item) => ({
      ...item,
      displayText: item.displayName || item.name || item.id,
      id: item.id,
      type: 'site' as const,
    }));
  }, [processedSites]);

  // Get counts from cached data
  const totalWans = mapState.dashboardData?.partner?.wans || 0;
  const totalControllers = mapState.dashboardData?.partner?.controllers?.count || 0;
  const totalSites = mapState.dashboardData?.partner?.sites?.count || 0;

  const alertSites = processedSites.filter((site) => site.status === 1 || site.status === 2 || site.status === 4);
  const alertControllers = processedControllers.filter(
    (controller) => controller.status === 1 || controller.status === 2 || controller.status === 4
  );
  const alertGateways = processedGateways.filter(
    (gateway) => gateway.status === 1 || gateway.status === 2 || gateway.status === 4
  );

  const alertTabs = [
    { id: 'sites' as const, label: 'Site Alerts', hasDown: alertSites.some((site) => site.status === 1) },
    {
      id: 'controllers' as const,
      label: 'Controller Alerts',
      hasDown: alertControllers.some((controller) => controller.status === 1),
    },
    {
      id: 'gateways' as const,
      label: 'Gateway Alerts',
      hasDown: alertGateways.some((gateway) => gateway.status === 1),
    },
  ];

  return (
    <Page
      tag="div"
      className={`cr-page px-3 pt-2 ${theme}`}
      title=""
      breadcrumbs={[{ name: 'Dashboard', active: true }]}
      actionButton={null}
    >
      <Row className="dashboard-main-row">
        <Col lg={3} md={4} sm={12} xs={12} className="mb-3 dashboard-summary-column">
          {loadingStates.overview || mapState.dashboardLoading ? (
            <Loader />
          ) : (
            <div className="dashboard-card-wrapper overview dashboard-summary-card">
              <h2>
                <strong>Overview</strong>
              </h2>
              <div className="dashboard-overview-list">
                <div onClick={() => history.push('/wans')} className="dashboard-overview-card">
                  <IconWidget
                    bgColor={theme === 'dark' ? 'dark' : 'white'}
                    inverse={false}
                    icon={MdOutlineImportExport}
                    iconProps={{ size: 42 }}
                    title={totalWans.toString()}
                    subtitle="Total pWANs"
                    className="w-100 p-2"
                    ip={undefined}
                  />
                </div>
                <div onClick={() => history.push('/controllers')} className="dashboard-overview-card">
                  <IconWidget
                    bgColor={theme === 'dark' ? 'dark' : 'white'}
                    inverse={false}
                    icon={MdNetworkCell}
                    iconProps={{ size: 42 }}
                    title={totalControllers.toString()}
                    subtitle="Total controllers"
                    className="w-100 p-2"
                    ip={undefined}
                  />
                </div>
                <div onClick={() => history.push('/sites')} className="dashboard-overview-card">
                  <IconWidget
                    bgColor={theme === 'dark' ? 'dark' : 'white'}
                    inverse={false}
                    icon={MdLanguage}
                    iconProps={{ size: 42 }}
                    title={totalSites.toString()}
                    subtitle="Total sites"
                    className="w-100 p-2"
                    ip={undefined}
                  />
                </div>
              </div>
            </div>
          )}

          {loadingStates.versionControl ? (
            <Loader />
          ) : (
            <div className="dashboard-card-wrapper dashboard-summary-card dashboard-version">
              <h2>
                <strong>Versions</strong>
              </h2>
              <ul className="dashboard-version-list">
                {versionItems.map((item) => (
                  <li key={item.key} className="dashboard-version-list-item">
                    <strong>{item.label}</strong>
                    <span>{versions[item.key]}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Col>

        <Col lg={9} md={8} sm={12} xs={12} className="mb-3 dashboard-map-column">
          {/* Deployment Map Section */}
          {loadingStates.heatmap || mapState.dashboardLoading ? (
            <Loader />
          ) : (
            <div className="dashboard-card-wrapper w-100 h-100 pb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2>
                  <strong>Deployment Map</strong>
                </h2>
              </div>
              <GoogleMapWithBubblesDashboard controllers={mapControllers} sites={mapSites} />
            </div>
          )}
        </Col>
      </Row>

      <Row>
        <Col xs={12} className="mb-3">
          {loadingStates.alerts || mapState.dashboardLoading ? (
            <Loader />
          ) : (
            <div className="dashboard-card-wrapper dashboard-alerts-panel">
              <div className="dashboard-alert-tabs" role="tablist" aria-label="Deployment alerts">
                {alertTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeAlertTab === tab.id}
                    className={`dashboard-alert-tab ${activeAlertTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveAlertTab(tab.id)}
                  >
                    {tab.label}
                    {tab.hasDown && (
                      <span className="dashboard-alert-down-indicator" aria-label="Contains down alerts" />
                    )}
                  </button>
                ))}
              </div>
              <div className="dashboard-alert-content site-status" role="tabpanel">
                {activeAlertTab === 'sites' &&
                  (alertSites.length ? (
                    alertSites.map((site) => (
                      <div className="me-2 mb-2" key={site.id}>
                        <Link to={'/site/' + site.id}>
                          <IconWidget
                            iconProps={undefined}
                            className={undefined}
                            bgColor={site.statusColor}
                            icon={MdLightbulbOutline}
                            title={site.displayName}
                            subtitle=""
                            ip={undefined}
                          />
                        </Link>
                      </div>
                    ))
                  ) : (
                    <div>None</div>
                  ))}
                {activeAlertTab === 'controllers' &&
                  (alertControllers.length ? (
                    alertControllers.map((controller) => (
                      <div className="me-2 mb-2" key={controller.id}>
                        <Link to={'/controller/' + controller.id}>
                          <IconWidget
                            iconProps={undefined}
                            className={undefined}
                            bgColor={controller.statusColor}
                            icon={MdLightbulbOutline}
                            title={controller.displayName}
                            subtitle=""
                            ip={undefined}
                          />
                        </Link>
                      </div>
                    ))
                  ) : (
                    <div>None</div>
                  ))}
                {activeAlertTab === 'gateways' &&
                  (alertGateways.length ? (
                    alertGateways.map((gateway) => (
                      <div
                        className="me-2 mb-2"
                        key={gateway.id}
                        onClick={async () => {
                          try {
                            const gatewayDetails = await GatewayApi.get(gateway.id);
                            if (gatewayDetails?.controller_id)
                              history.push(`/controller/${gatewayDetails.controller_id}`);
                          } catch (error) {
                            console.error('Error fetching gateway details:', error);
                          }
                        }}
                      >
                        <IconWidget
                          iconProps={undefined}
                          className="cursor-pointer"
                          bgColor={gateway.statusColor}
                          icon={MdLightbulbOutline}
                          title={gateway.ip_address}
                          subtitle=""
                          ip={undefined}
                        />
                      </div>
                    ))
                  ) : (
                    <div>None</div>
                  ))}
              </div>
            </div>
          )}
        </Col>
      </Row>
    </Page>
  );
};

export { DashboardView };
